import React, { useState } from 'react';
import { X, Brain, ArrowRight, Sparkles, Gift, CheckCircle, Loader2, PartyPopper } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AIDemoUnlockModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    organisation: '',
    phone: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  const validateStep = () => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Name is required';
      if (!formData.organisation.trim()) newErrors.organisation = 'Organisation is required';
    } else if (step === 2) {
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(2);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      // Create AI lead for demo access
      const response = await fetch(`${API_URL}/api/ai/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          company: formData.organisation,
          phone: formData.phone,
          feature_interest: 'ai_demo'
        })
      });

      if (!response.ok) throw new Error('Failed to submit');
      
      const data = await response.json();

      // Store in localStorage
      localStorage.setItem('contact_submitted', 'true');
      localStorage.setItem('contact_email', formData.email);
      localStorage.setItem('ai_lead_id', data.lead_id);
      
      setStep(3); // Success step
      
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2500);
      
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const resetAndClose = () => {
    setStep(1);
    setFormData({ fullName: '', organisation: '', phone: '', email: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-bounceIn">
        {/* Close Button */}
        <button
          onClick={resetAndClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all"
          data-testid="close-ai-unlock-modal"
        >
          <X size={18} />
        </button>

        {/* Step 1: Name & Organisation */}
        {step === 1 && (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-purple-600 via-tech-blue to-care-teal p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Brain className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Unlock AI Demo</h2>
              <p className="text-white/80 text-sm">Step 1 of 2 - Tell us about yourself</p>
            </div>

            {/* Progress Bar */}
            <div className="px-6 pt-4">
              <div className="flex gap-2">
                <div className="flex-1 h-2 rounded-full bg-purple-500"></div>
                <div className="flex-1 h-2 rounded-full bg-gray-200"></div>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              <div className={`relative transition-all duration-300 ${focusedField === 'fullName' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="John Smith"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  onFocus={() => setFocusedField('fullName')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.fullName ? 'border-red-400 bg-red-50' : focusedField === 'fullName' ? 'border-purple-500 bg-purple-50/30' : 'border-gray-200'}`}
                  data-testid="ai-unlock-name"
                />
                {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>}
              </div>

              <div className={`relative transition-all duration-300 ${focusedField === 'organisation' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisation <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Your Company Pty Ltd"
                  value={formData.organisation}
                  onChange={(e) => handleInputChange('organisation', e.target.value)}
                  onFocus={() => setFocusedField('organisation')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.organisation ? 'border-red-400 bg-red-50' : focusedField === 'organisation' ? 'border-purple-500 bg-purple-50/30' : 'border-gray-200'}`}
                  data-testid="ai-unlock-org"
                />
                {errors.organisation && <p className="text-red-500 text-xs mt-1">{errors.organisation}</p>}
              </div>

              <Button
                onClick={handleNext}
                className="w-full bg-gradient-to-r from-purple-600 to-tech-blue hover:from-purple-600/90 hover:to-tech-blue/90 text-white py-6 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                data-testid="ai-unlock-continue"
              >
                Continue
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </>
        )}

        {/* Step 2: Phone & Email */}
        {step === 2 && (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-care-teal via-tech-blue to-purple-600 p-6 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="text-white" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Almost There!</h2>
              <p className="text-white/80 text-sm">Step 2 of 2 - Contact details</p>
            </div>

            {/* Progress Bar */}
            <div className="px-6 pt-4">
              <div className="flex gap-2">
                <div className="flex-1 h-2 rounded-full bg-purple-500"></div>
                <div className="flex-1 h-2 rounded-full bg-purple-500"></div>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              <div className={`relative transition-all duration-300 ${focusedField === 'phone' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  placeholder="04XX XXX XXX"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.phone ? 'border-red-400 bg-red-50' : focusedField === 'phone' ? 'border-care-teal bg-care-teal/5' : 'border-gray-200'}`}
                  data-testid="ai-unlock-phone"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              <div className={`relative transition-all duration-300 ${focusedField === 'email' ? 'scale-[1.02]' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  placeholder="john@company.com.au"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-all ${errors.email ? 'border-red-400 bg-red-50' : focusedField === 'email' ? 'border-care-teal bg-care-teal/5' : 'border-gray-200'}`}
                  data-testid="ai-unlock-email"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 py-6 rounded-xl text-lg border-2"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-[2] bg-gradient-to-r from-purple-600 to-care-teal hover:from-purple-600/90 hover:to-care-teal/90 text-white py-6 rounded-xl text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                  data-testid="ai-unlock-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={20} />
                      Unlocking...
                    </>
                  ) : (
                    <>
                      <Brain className="mr-2" size={20} />
                      Access AI Demo
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="p-8 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-care-teal rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <PartyPopper className="text-white" size={48} />
            </div>
            <h2 className="text-2xl font-bold text-primary-navy mb-3">Success!</h2>
            <p className="text-text-secondary mb-4">
              Thank you, <span className="font-semibold text-purple-600">{formData.fullName}</span>!
            </p>
            <p className="text-text-secondary mb-6">
              AI Demo is now unlocked. Loading your access...
            </p>
            <div className="flex justify-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-600 animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-3 h-3 rounded-full bg-tech-blue animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-3 h-3 rounded-full bg-care-teal animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        )}

        {/* Benefits Footer - Only show on step 1 & 2 */}
        {(step === 1 || step === 2) && (
          <div className="px-6 pb-6">
            <div className="bg-gradient-to-r from-purple-500/10 to-care-teal/10 rounded-xl p-4 border border-purple-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Gift className="text-purple-600" size={18} />
                <span className="text-sm font-semibold text-primary-navy">What you'll get access to:</span>
              </div>
              <ul className="space-y-1">
                <li className="flex items-center gap-2 text-xs text-text-secondary">
                  <CheckCircle className="text-care-teal" size={12} />
                  AI Compliance Engine - Document analysis
                </li>
                <li className="flex items-center gap-2 text-xs text-text-secondary">
                  <CheckCircle className="text-care-teal" size={12} />
                  Incident Pattern Detection - Risk prediction
                </li>
                <li className="flex items-center gap-2 text-xs text-text-secondary">
                  <CheckCircle className="text-care-teal" size={12} />
                  Predictive Workforce Planning - Smart staffing
                </li>
                <li className="flex items-center gap-2 text-xs text-text-secondary">
                  <CheckCircle className="text-care-teal" size={12} />
                  AI Executive Reports & Smart Notifications
                </li>
              </ul>
            </div>
          </div>
        )}

        <style jsx>{`
          @keyframes bounceIn {
            0% { opacity: 0; transform: scale(0.8); }
            50% { transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
          }
          .animate-bounceIn {
            animation: bounceIn 0.5s ease-out;
          }
        `}</style>
      </div>
    </div>
  );
};

export default AIDemoUnlockModal;
